import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  localISODate,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type PurchaseOrder = {
  id: number
  number: string
  partner: number
  partner_name?: string
  order_date: string
  expected_delivery_date: string | null
  status: 'DRAFT' | 'ISSUED' | 'PARTIAL_RECEIVED' | 'RECEIVED' | 'CLOSED' | 'CANCELLED'
  currency_code: string
  total: string
  vendor_reference?: string
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }

// Minimal create payload — rest filled in on the record page.
type CreateInput = {
  number: string
  partner: number | 0
  order_date: string
  expected_delivery_date: string | null
  currency_code: string
}

const STATUS_COLOR: Record<PurchaseOrder['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ISSUED: 'bg-blue-100 text-blue-800',
  PARTIAL_RECEIVED: 'bg-amber-100 text-amber-800',
  RECEIVED: 'bg-green-100 text-green-800',
  CLOSED: 'bg-purple-100 text-purple-800',
  CANCELLED: 'bg-red-100 text-red-700',
}

const STATUS_FILTERS: ('ALL' | PurchaseOrder['status'])[] = [
  'ALL', 'DRAFT', 'ISSUED', 'PARTIAL_RECEIVED', 'RECEIVED', 'CLOSED', 'CANCELLED',
]

export default function ProcurementPurchaseOrders() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const ordersQ = useQuery({
    queryKey: ['procurement', 'purchase-orders'],
    queryFn: async () => (await api.get<PurchaseOrder[]>('/procurement/purchase-orders/')).data,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterStatus, setFilterStatus] = useState<'ALL' | PurchaseOrder['status']>('ALL')

  function openCreate() {
    setDraft({
      ...emptyDraft(),
      number: `PO-${Date.now().toString().slice(-6)}`,
    })
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<PurchaseOrder>('/procurement/purchase-orders/', {
      ...draft,
      partner: Number(draft.partner),
      status: 'DRAFT',
      delivery_address: '',
      notes: '',
      lines: [],
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['procurement', 'purchase-orders'] })
      toast.success('Purchase order created')
      setCreating(false)
      navigate(`/admin/procurement/purchase-orders/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/procurement/purchase-orders/${id}/`),
    onSuccess: () => {
      toast.success('Purchase order deleted')
      qc.invalidateQueries({ queryKey: ['procurement', 'purchase-orders'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const issueMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/procurement/purchase-orders/${id}/issue/`)).data,
    onSuccess: () => {
      toast.success('Purchase order issued')
      qc.invalidateQueries({ queryKey: ['procurement', 'purchase-orders'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const orders = ordersQ.data || []
  const partners = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'VENDOR' || p.partner_type === 'BOTH' || p.partner_type === 'FREELANCER')
  )
  const filtered = filterStatus === 'ALL'
    ? orders
    : orders.filter((o) => o.status === filterStatus)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Purchase Orders</h2>
          <p className="text-sm text-muted-foreground">
            {orders.length} total · {orders.filter((o) => o.status === 'ISSUED' || o.status === 'PARTIAL_RECEIVED').length} in flight
          </p>
        </div>
        <Button onClick={openCreate} disabled={partners.length === 0}>
          <Plus className="h-3 w-3 mr-1" /> New purchase order
        </Button>
      </div>

      {partners.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-lg mb-4">
          Add at least one active <strong>vendor</strong> under Partners before creating purchase orders.
        </div>
      )}

      <div className="mb-4 flex items-center space-x-2 text-sm flex-wrap gap-y-2">
        <span className="text-muted-foreground">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase().replace('_', ' ')}
          </button>
        ))}
      </div>

      {ordersQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No purchase orders.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Number</Th>
                <Th>Date</Th>
                <Th>Vendor</Th>
                <Th>Vendor ref</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/procurement/purchase-orders/${o.id}`)}
                >
                  <Td className="font-mono text-xs">{o.number}</Td>
                  <Td className="text-xs">{o.order_date}</Td>
                  <Td>{o.partner_name}</Td>
                  <Td className="text-xs font-mono">{o.vendor_reference || <span className="text-muted-foreground">—</span>}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[o.status]}`}>{o.status}</span>
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/procurement/purchase-orders/${o.id}`)}>
                          Open
                        </DropdownMenuItem>
                        {o.status === 'DRAFT' && (
                          <DropdownMenuItem
                            onClick={() => confirm(
                              `Issue purchase order ${o.number} (locks lines)`,
                              () => issueMut.mutate(o.id),
                            )}
                            disabled={issueMut.isPending}
                            className="text-blue-700"
                          >
                            Issue PO
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={o.status !== 'DRAFT' && o.status !== 'CANCELLED'}
                          onClick={() => (o.status === 'DRAFT' || o.status === 'CANCELLED') && confirm(
                            `purchase order ${o.number}`,
                            () => deleteMut.mutate(o.id),
                          )}
                          className={(o.status === 'DRAFT' || o.status === 'CANCELLED') ? 'text-destructive' : ''}
                        >
                          {(o.status === 'DRAFT' || o.status === 'CANCELLED') ? 'Delete' : 'Delete (issued)'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Lines + delivery + notes on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New purchase order"
        description="Create the PO header. Add line items, delivery address, and notes on the record page. Issue when ready to send to the vendor."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="po-num">Number *</FieldLabel>
              <Input id="po-num" autoFocus required value={draft.number}
                onChange={(ev) => setDraft({ ...draft, number: ev.target.value })}
                className="font-mono" />
            </Field>
            <Field>
              <FieldLabel htmlFor="po-cur">Currency</FieldLabel>
              <Input id="po-cur" value={draft.currency_code}
                onChange={(ev) => setDraft({ ...draft, currency_code: ev.target.value.toUpperCase().slice(0, 3) })}
                className="font-mono" maxLength={3} />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="po-vendor">Vendor *</FieldLabel>
            <SearchableSelect
              id="po-vendor"
              value={draft.partner ? String(draft.partner) : undefined}
              onChange={(v) => setDraft({ ...draft, partner: Number(v) })}
              placeholder="— select —"
              options={partners.map((p) => ({
                value: String(p.id), label: p.name, hint: p.code,
              }))}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="po-date">Order date *</FieldLabel>
              <DatePicker id="po-date" required value={draft.order_date}
                onChange={(v) => setDraft({ ...draft, order_date: v })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="po-delivery">Expected delivery</FieldLabel>
              <DatePicker id="po-delivery" value={draft.expected_delivery_date || ''}
                onChange={(v) => setDraft({ ...draft, expected_delivery_date: v || null })} />
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    number: '',
    partner: 0,
    order_date: localISODate(new Date()),
    expected_delivery_date: null,
    currency_code: 'EUR',
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
